import React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { fontSizes } from '@/theme/typography'
import { black, lightGrey, orange, textMuted } from '@/theme/colors'
import { media, motion } from '@/theme/tokens'

export interface BreadcrumbItem {
	label: string
	path?: string
}

interface BreadcrumbsProps {
	items: BreadcrumbItem[]
	/** Rendered at the right edge of the row — a page-level status chip. */
	trailing?: React.ReactNode
}

const BreadcrumbsContainer = styled.nav`
	display: flex;
	align-items: center;
	padding: 16px 0;
	font-size: ${fontSizes.small};
	color: ${textMuted};
	background: transparent;

	${media.phone} {
		padding: 10px 0;
		font-size: ${fontSizes.caption};
	}
`

const BreadcrumbList = styled.ol`
	display: flex;
	align-items: center;
	list-style: none;
	margin: 0;
	padding: 0;
	gap: 8px;
	flex-wrap: wrap;

	${media.phone} {
		gap: 6px;
	}
`

/* A long trip name wraps; an unbroken one breaks mid-word rather than pushing the trail off the page. */
const BreadcrumbItem = styled.li`
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
	overflow-wrap: anywhere;

	${media.phone} {
		gap: 6px;
	}
`

const BreadcrumbLink = styled(Link)`
	color: ${textMuted};
	text-decoration: none;
	transition: color ${motion.base};

	&:hover {
		color: ${orange};
	}
`

const BreadcrumbCurrent = styled.span`
	color: ${black};
	font-weight: 500;
`

/* Pushed to the far edge so the chip sits opposite the trail. */
const TrailingSlot = styled.div`
	margin-left: auto;
	padding-left: 16px;
	display: flex;
	align-items: center;
	flex-shrink: 0;
`

const BreadcrumbSeparator = styled.span`
	color: ${lightGrey};
	user-select: none;
`

const ChevronRight = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
		<path
			fillRule="evenodd"
			d="M6.22 3.22a.75.75 0 011.06 0l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-1.06-1.06L9.94 8 6.22 4.28a.75.75 0 010-1.06z"
		/>
	</svg>
)

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items, trailing }) => {
	if (items.length === 0) return null

	return (
		<BreadcrumbsContainer aria-label="Breadcrumb">
			<BreadcrumbList>
				{items.map((item, index) => {
					const isLast = index === items.length - 1

					return (
						<BreadcrumbItem key={index}>
							{!isLast && item.path ? (
								<>
									<BreadcrumbLink to={item.path}>{item.label}</BreadcrumbLink>
									<BreadcrumbSeparator>
										<ChevronRight />
									</BreadcrumbSeparator>
								</>
							) : (
								<BreadcrumbCurrent aria-current="page">{item.label}</BreadcrumbCurrent>
							)}
						</BreadcrumbItem>
					)
				})}
			</BreadcrumbList>
			{trailing && <TrailingSlot>{trailing}</TrailingSlot>}
		</BreadcrumbsContainer>
	)
}

export default Breadcrumbs

